import React from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import {
  Search,
  Shield,
  Bell,
  CheckCircle2,
  Clock,
  DollarSign,
  Users,
  MapPin,
  Home as HomeIcon,
  Sparkles,
} from 'lucide-react';

const Home = () => {
  const handleCTA = () => {
    toast.success('Coming Soon! We have captured your interest.', {
      description: 'We\'ll notify you when we launch.',
      duration: 4000,
    });
  };

  const areas = [
    'Indiranagar',
    'Koramangala',
    'HSR Layout',
    'Bellandur',
    'JP Nagar',
  ];

  const features = [
    {
      icon: Shield,
      title: 'Verified by humans and AI, not just bots',
      description: 'Cross-checked by local verifiers and AI across all major platforms',
    },
    {
      icon: DollarSign,
      title: 'Saves ₹10k+ in subscription fees',
      description: 'No heavy portal subscriptions, completely free during beta',
    },
    {
      icon: Clock,
      title: 'Updated every week',
      description: 'Fresh verified listings delivered weekly via WhatsApp and Sheets',
    },
    {
      icon: CheckCircle2,
      title: '100% broker-free discovery',
      description: 'Direct owner contacts, zero broker commissions or spam',
    },
  ];

  const faqData = [
    {
      question: 'Is flatscout free?',
      answer:
        'Yes — early access is completely free during our beta phase. We believe finding a home should be accessible to everyone.',
    },
    {
      question: 'How often is data updated?',
      answer:
        'Our AI agents scan all platforms weekly, plus you get instant WhatsApp alerts for new verified listings as they appear.',
    },
    {
      question: 'How are listings verified?',
      answer:
        'Our AI compares listings across 10+ platforms, removes duplicates, checks for inconsistencies, and flags suspicious posts to ensure only real listings reach you.',
    },
    {
      question: 'Which cities are supported?',
      answer:
        'Currently, we focus on Bengaluru. More cities are coming soon based on user demand.',
    },
    {
      question: 'How can I get access?',
      answer:
        'Join our WhatsApp group or request access to our Google Sheet with verified listings. Early beta users get lifetime perks!',
    },
  ];

  return (
    <div className="home-container">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_rental-scout-3/artifacts/pb4liabe_image.png" 
              alt="Flatscout Logo" 
              className="logo-image"
            />
            <span className="logo-text">flatscout</span>
          </div>
          <Button onClick={handleCTA} className="cta-button">
            Join Early Access
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-gradient"></div>
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles className="w-4 h-4" />
            <span>Find Your Next Home in Under 10 Minutes</span>
          </div>
          <h1 className="hero-title">
            Find <span className="gradient-text">Verified Flats</span> in
            Bengaluru - Before Anyone Else.
          </h1>
          <p className="hero-subtitle">
            flatscout's human network and AI agents scan 10+ platforms, check listings on-ground, validate details, and deliver only real 1-2–3BHK homes.<br />
            <strong>No brokers. No scams. No wasted weekends.</strong> Just share your requirements with us. Today!
          </p>
          <div className="hero-buttons">
            <Button onClick={handleCTA} className="primary-button">
              🔍 Get Verified Listings
            </Button>
            <Button onClick={handleCTA} variant="outline" className="secondary-button">
              💬 Join our WhatsApp Group
            </Button>
          </div>
          <div className="platform-strip">
            <span className="platform-strip-label">Cross-checked from:</span>
            <span className="platform-badge">NoBroker</span>
            <span className="platform-badge">99acres</span>
            <span className="platform-badge">MagicBricks</span>
            <span className="platform-badge">Housing</span>
            <span className="platform-badge">Facebook</span>
            <span className="platform-badge">Reddit</span>
            <span className="platform-badge">WhatsApp</span>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section how-it-works">
        <div className="section-container">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">
            Real people. Real homes. Verified every week.
          </p>
          
          {/* Visual Explanation */}
          <div className="aggregation-visual">
            <img 
              src="https://customer-assets.emergentagent.com/job_rental-scout-3/artifacts/agzzoqai_image.png" 
              alt="Flatscout aggregates listings from 10+ platforms" 
              className="aggregation-image"
            />
          </div>
          
          <div className="steps-grid-four">
            <div className="step-card-square">
              <div className="step-icon-large">
                <Search className="w-10 h-10" />
              </div>
              <h3 className="step-title">We Search Everywhere</h3>
              <p className="step-description">
                We scout listings from 10+ portals and communities every day.
              </p>
            </div>
            <div className="step-card-square">
              <div className="step-icon-large">
                <Shield className="w-10 h-10" />
              </div>
              <h3 className="step-title">Human + Community Verification</h3>
              <p className="step-description">
                Local tenants and verifiers confirm rent, owners, and flat status.
              </p>
            </div>
            <div className="step-card-square">
              <div className="step-icon-large">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h3 className="step-title">Cross-Checked & Cleaned Up</h3>
              <p className="step-description">
                Duplicates, scams, and stale listings are filtered out.
              </p>
            </div>
            <div className="step-card-square">
              <div className="step-icon-large">
                <Bell className="w-10 h-10" />
              </div>
              <h3 className="step-title">Delivered Weekly</h3>
              <p className="step-description">
                Get curated updates via WhatsApp and Google Sheets.
              </p>
            </div>
          </div>
          <p className="how-it-works-tagline">Real people. Real homes. Verified weekly.</p>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="section areas-section">
        <div className="section-container">
          <h2 className="section-title">Areas We Cover</h2>
          <p className="section-subtitle">
            Premium localities across Bengaluru
          </p>
          <div className="areas-grid">
            {areas.map((area, index) => (
              <div key={index} className="area-badge">
                <MapPin className="w-5 h-5" />
                <span>{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why tenants trust flatscout */}
      <section className="section features-section">
        <div className="section-container">
          <h2 className="section-title">Why tenants trust flatscout</h2>
          <p className="section-subtitle">
            Honest, verified, and built for people tired of fake listings
          </p>
          <div className="features-grid">
            {features.map((feature, index) => (
              <Card key={index} className="feature-card">
                <div className="feature-icon">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section faq-section">
        <div className="section-container-narrow">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Everything you need to know</p>
          <Accordion type="single" collapsible className="faq-accordion">
            {faqData.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="faq-question">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Early Access CTA */}
      <section className="section cta-section">
        <div className="cta-card">
          <div className="cta-content">
            <h2 className="cta-title">Built by tenants, for tenants ❤️</h2>
            <p className="cta-description">
              We've all spent weeks scrolling through fake or outdated listings. flatscout combines human verification with AI to cross-check every listing across 99acres, NoBroker, Housing, Reddit, WhatsApp, and Telegram groups. Everything is honest, no ads, and 100% genuine.
            </p>
            <Button onClick={handleCTA} className="primary-button cta-main-button">
              Join Early Access — Start Finding Verified Homes
            </Button>
            <p className="cta-subtext">
              Limited beta spots available. Early users get lifetime access perks.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="logo-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_rental-scout-3/artifacts/pb4liabe_image.png" 
                alt="Flatscout Logo" 
                className="logo-image"
              />
              <span className="logo-text">flatscout</span>
            </div>
            <p className="footer-tagline">
              Find your next flat in &lt;10 minutes
            </p>
          </div>
          <div className="footer-links">
            <a href="#" onClick={(e) => { e.preventDefault(); handleCTA(); }} className="footer-link">
              Contact
            </a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleCTA(); }} className="footer-link">
              Privacy
            </a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleCTA(); }} className="footer-link">
              Terms
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2025 flatscout. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
